import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Flex, Form, Input, Modal, Table, TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { PagedResultState } from '../../../common/CommonResult';
import { API } from '../../../api';
import queryString from 'query-string';
import { notifyError, showConfirm } from '../../../store/utils';
import { Link } from 'react-router-dom';

interface Filter {
  page: number;
  size: number;
  query?: string;
}

interface DeliveryType {
  visible: boolean;
  no?: number;
}

interface AreaType {
  visible: boolean;
  areaNo?: number;
  branchArea?: any;
}

const AreaList = () => {
  const [filter, setFilter] = useState<Filter>({
    page: 1,
    size: 10,
  });
  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const [delivery, setDelivery] = useState<DeliveryType>({
    visible: false,
  });

  const [area, setArea] = useState<AreaType>({
    visible: false,
  });

  useEffect(() => {
    fetchAreaList();
  }, [filter]);

  const fetchAreaList = useCallback(async () => {
    setState({ ...state, loading: true });
    try {
      const response = await API.get({ apiVersion: 'api' })(
        `/settings/branch_area/list?${queryString.stringify(filter)}`
      );
      const list: any[] = [];
      (response.result?.data || []).forEach((element: any) => {
        if ((element?.areas || []).length) {
          (element?.areas || []).forEach((item: any, index: number) => {
            list.push({
              ...element,
              rowSpan: index == 0 ? element.areas.length : 0,
              areaName: item.name,
              areaCode: item.cd,
              areaNo: item.no,
            });
          });
        } else {
          list.push({
            ...element,
            rowSpan: 1,
          });
        }
      });
      setState({
        loading: false,
        result: {
          page: response.result?.page || 1,
          size: response.result?.size || 10,
          total: response.result?.total || 1,
          data: list,
        },
      });
    } catch (error: any) {
      setState({ ...state, loading: true, error: true });
    }
  }, [state, filter]);

  const showDeleteConfirm = useCallback((record: any) => {
    showConfirm({
      title: 'Устгахдаа итгэлтэй байна уу?',
      content: 'Уг үйлдэл нь буцаагдахгүй!. Бүсийн тохиргоо бүгд устахыг анхаарна уу',
      onOk() {
        areaItemDelete(record.areaNo);
      },
      okText: 'Тийм',
      cancelText: 'Үгүй',
    });
  }, []);

  const areaItemDelete = useCallback(async (areaNo: number) => {
    try {
      await API.delete({ apiVersion: 'api' })(`/settings/courier_area/${areaNo}`);
      fetchAreaList();
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const columns: TableColumnsType<any> = [
    {
      title: 'Хүргэлт',
      dataIndex: 'name',
      key: 'name',
      onCell: (record, index = 0) =>
        record?.rowSpan ? { rowSpan: record.rowSpan } : { rowSpan: 0 },
      width: 200,
    },
    {
      title: 'Хү.код',
      dataIndex: 'cd',
      key: 'cd',
      width: 70,
      onCell: (record, index = 0) =>
        record?.rowSpan ? { rowSpan: record.rowSpan } : { rowSpan: 0 },
    },
    {
      title: 'Үйлдэл',
      key: 'action',
      onCell: (record, index = 0) =>
        record?.rowSpan ? { rowSpan: record.rowSpan } : { rowSpan: 0 },
      width: 120,
      render: (record: any) => {
        return (
          <Flex gap={12} vertical={true}>
            <Button size="small" onClick={() => setDelivery({ visible: true, no: record.no })}>
              Засах
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => setArea({ visible: true, branchArea: record })}
            >
              Нэмэх
            </Button>
            {/* <Button
              size="small"
              danger
              type="primary"
            >
              Устгах
            </Button> */}
          </Flex>
        );
      },
    },
    {
      title: 'Бүс',
      dataIndex: 'areaName',
      key: 'areaName',
    },
    {
      title: 'Бүсийн код',
      dataIndex: 'areaCode',
      key: 'areaCode',
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      width: 200,
      render: (record: any) => {
        return (
          <>
            {record.areaNo ? (
              <Flex gap={12}>
                <Link to={`/settings/area/address/${record.areaNo}`}>
                  <Button size="small">Хаяг</Button>
                </Link>
                <Button
                  size="small"
                  onClick={() =>
                    setArea({
                      visible: true,
                      branchArea: record,
                      areaNo: record.areaNo,
                    })
                  }
                >
                  Засах
                </Button>
                <Button
                  size="small"
                  type="primary"
                  danger
                  onClick={() => showDeleteConfirm(record)}
                >
                  Устгах
                </Button>
              </Flex>
            ) : null}
          </>
        );
      },
    },
  ];

  return (
    <Card
      title={`Бүсийн жагсаалт`}
      extra={
        <Button
          type="primary"
          onClick={() => setDelivery({ visible: true })}
          icon={<PlusOutlined />}
        >
          Хүргэлт нэмэх
        </Button>
      }
    >
      <Table
        bordered
        size="small"
        loading={state.loading}
        columns={columns}
        dataSource={state?.result?.data || []}
      />
      <CreateDeliveryArea
        visible={delivery.visible}
        no={delivery.no}
        reload={() => fetchAreaList()}
        onClose={() => setDelivery({ visible: false })}
      />
      <UpdateArea
        visible={area.visible}
        areaNo={area.areaNo}
        branchArea={area.branchArea}
        onClose={() => setArea({ visible: false })}
        reload={() => fetchAreaList()}
      />
    </Card>
  );
};

interface CreateDeliveryAreaProps {
  visible: boolean;
  no?: number;
  reload?: () => void;
  onClose: () => void;
}

const CreateDeliveryArea = ({ visible, no, reload, onClose }: CreateDeliveryAreaProps) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = useCallback(
    async (values: any) => {
      try {
        setLoading(true);
        if (!no) {
          await API.post({ apiVersion: 'api' })(`/settings/branch_area`, values);
        } else {
          await API.put({ apiVersion: 'api' })(`/settings/branch_area/${no}`, values);
        }
        setLoading(false);
        close();
        if (reload) reload();
      } catch (error: any) {
        notifyError(error.message);
        setLoading(false);
      }
    },
    [no, reload]
  );

  const fetchData = useCallback(
    async (itemNo: number) => {
      try {
        const { result } = await API.get({ apiVersion: 'api' })(`/settings/branch_area/${itemNo}`);
        form.setFieldsValue({
          name: result?.data?.name,
          cd: result?.data?.cd,
        });
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [form]
  );

  const close = useCallback(() => {
    form.resetFields();
    onClose();
  }, []);

  useEffect(() => {
    if (no) {
      fetchData(no);
    }
  }, [no]);

  return (
    <Modal
      title={no ? 'Хүргэлт засах' : 'Хүргэлт нэмэх'}
      open={visible}
      onCancel={close}
      footer={null}
    >
      <Divider />
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="name"
          label="Хүргэлтийн нэр"
          rules={[{ required: true, message: 'Хүргэлтийн нэр оруулна уу' }]}
        >
          <Input placeholder="Хүргэлтийн нэр оруулна уу" />
        </Form.Item>
        <Form.Item
          name="cd"
          label="Хүргэлтийн код"
          rules={[{ required: true, message: 'Хүргэлтийн код оруулна уу' }]}
        >
          <Input disabled={no ? true : false} placeholder="Хүргэлтийн код оруулна уу" />
        </Form.Item>
        <Form.Item>
          <Flex justify="end">
            <Button loading={loading} type="primary" htmlType="submit">
              Хадгалах
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Modal>
  );
};

interface UpdateAreaProps {
  visible: boolean;
  areaNo?: number;
  branchArea?: any;
  reload?: () => void;
  onClose: () => void;
}

const UpdateArea = ({ visible, areaNo, branchArea, reload, onClose }: UpdateAreaProps) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = useCallback(
    async (values: any) => {
      try {
        setLoading(true);
        const payload = {
          ...values,
          branchAreaNo: branchArea.no,
        };
        if (!areaNo) {
          await API.post({ apiVersion: 'api' })(`/settings/courier_area`, payload);
        } else {
          await API.put({ apiVersion: 'api' })(`/settings/courier_area/${areaNo}`, payload);
        }
        setLoading(false);
        close();
        if (reload) reload();
      } catch (error: any) {
        notifyError(error.message);
        setLoading(false);
      }
    },
    [areaNo, branchArea, reload]
  );

  const fetchData = useCallback(
    async (itemNo: number) => {
      try {
        const { result } = await API.get({ apiVersion: 'api' })(`/settings/courier_area/${itemNo}`);
        form.setFieldsValue({
          name: result?.data?.name,
          cd: result?.data?.cd,
        });
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [form]
  );

  const close = useCallback(() => {
    form.resetFields();
    onClose();
  }, []);

  useEffect(() => {
    if (areaNo) {
      fetchData(areaNo);
    }
  }, [areaNo]);

  return (
    <Modal title={branchArea?.name} open={visible} onCancel={close} footer={null}>
      <Divider />
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="name"
          label="Бүсийн нэр"
          rules={[{ required: true, message: 'Бүсийн нэр оруулна уу' }]}
        >
          <Input placeholder="Бүсийн нэр оруулна уу" />
        </Form.Item>
        <Form.Item
          name="cd"
          label="Бүсийн код"
          rules={[{ required: true, message: 'Бүсийн код оруулна уу' }]}
        >
          <Input disabled={areaNo ? true : false} placeholder="Бүсийн код оруулна уу" />
        </Form.Item>
        <Form.Item>
          <Flex justify="end">
            <Button loading={loading} type="primary" htmlType="submit">
              Хадгалах
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AreaList;
